import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthResponse } from '../domain/auth-user';
import { PasskeyOptionsResponse } from '../domain/security/passkey.dtos';
import {
  LoginResult,
  TwoFactorAuthResponse,
  TwoFactorRequired,
} from '../domain/security/two-factor.dtos';
import { UserInfo } from '../domain/user-info';
import { StorageService } from './storage.service';

/**
 * Trusted-device tokens deliberately live in localStorage, not sessionStorage:
 * "remember this device for 30 days" must survive browser restarts. The token
 * is opaque (server stores only its hash) and grants nothing by itself — it
 * only skips the 2FA prompt when paired with a correct password.
 */
const TRUSTED_DEVICE_KEY = 'edjoin_trusted_device';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);

  private _isRefreshing = false;
  private _refreshDone$ = new BehaviorSubject<boolean>(false);

  get isRefreshing(): boolean {
    return this._isRefreshing;
  }

  get refreshDone$(): Observable<boolean> {
    return this._refreshDone$.asObservable();
  }

  getToken(): string | null {
    return sessionStorage.getItem('token');
  }

  async getRefreshToken(): Promise<string | null> {
    return this.storage.get<string>('refreshToken');
  }

  /**
   * Sign in with username/password. Resolves to a discriminated result:
   * accounts with 2FA enabled get a challenge instead of a session (HTTP 403
   * with code "2fa_required" — mapped here, not thrown). Wrong credentials
   * and other failures still reject.
   */
  async login(credentials: { username: string; password: string }): Promise<LoginResult> {
    try {
      const auth = await firstValueFrom(
        this.http.post<AuthResponse>(`${environment.apiUrl}/auth/login`, {
          ...credentials,
          trustedDeviceToken: this.getTrustedDeviceToken(),
        }),
      );
      await this.processAuth(auth);
      return { kind: 'success' };
    } catch (err) {
      const challenge = extractTwoFactorChallenge(err);
      if (challenge) {
        // A challenge despite sending a device token means the token is
        // expired or revoked — drop it so we stop sending a dead token.
        this.clearTrustedDeviceToken();
        return { kind: 'twoFactorRequired', challenge };
      }
      throw err;
    }
  }

  /** Anonymous usernameless passkey assertion options ("Sign in with a passkey"). */
  async getPasskeyLoginOptions(): Promise<PasskeyOptionsResponse> {
    return firstValueFrom(
      this.http.post<PasskeyOptionsResponse>(`${environment.apiUrl}/auth/passkey/options`, {}),
    );
  }

  /**
   * Complete a passkey sign-in with the browser's assertion. A user-verified
   * passkey satisfies 2FA on its own, so no challenge step follows (WI 43357).
   */
  async loginWithPasskey(optionsToken: string, response: unknown): Promise<void> {
    const auth = await firstValueFrom(
      this.http.post<AuthResponse>(`${environment.apiUrl}/auth/passkey/login`, {
        optionsToken,
        response,
      }),
    );
    await this.processAuth(auth);
  }

  /** Complete a login 2FA challenge with a 6-digit code. */
  async verifyTwoFactor(request: {
    challengeToken: string;
    code: string;
    rememberDevice: boolean;
  }): Promise<void> {
    const auth = await firstValueFrom(
      this.http.post<TwoFactorAuthResponse>(`${environment.apiUrl}/auth/2fa/verify`, request),
    );
    await this.finishTwoFactor(auth);
  }

  /** Complete a login 2FA challenge with an unused recovery code. */
  async useRecoveryCode(request: {
    challengeToken: string;
    recoveryCode: string;
    rememberDevice: boolean;
  }): Promise<void> {
    const auth = await firstValueFrom(
      this.http.post<TwoFactorAuthResponse>(`${environment.apiUrl}/auth/2fa/recovery`, request),
    );
    await this.finishTwoFactor(auth);
  }

  /** Email method only: rotate and re-send the challenge code (max 3 per challenge). */
  async resendTwoFactorCode(challengeToken: string): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`${environment.apiUrl}/auth/2fa/resend`, { challengeToken }),
    );
  }

  private async finishTwoFactor(auth: TwoFactorAuthResponse): Promise<void> {
    await this.processAuth(auth);
    if (auth.trustedDeviceToken) {
      this.storeTrustedDeviceToken(auth.trustedDeviceToken);
    }
  }

  private getTrustedDeviceToken(): string | null {
    // localStorage can throw in private-browsing / quota-exceeded situations.
    try {
      return localStorage.getItem(TRUSTED_DEVICE_KEY);
    } catch {
      return null;
    }
  }

  private storeTrustedDeviceToken(token: string): void {
    try {
      localStorage.setItem(TRUSTED_DEVICE_KEY, token);
    } catch {
      // Losing the token just means the user is prompted for a code next time.
    }
  }

  private clearTrustedDeviceToken(): void {
    try {
      localStorage.removeItem(TRUSTED_DEVICE_KEY);
    } catch {
      // Ignore: worst case a dead token keeps being sent and ignored.
    }
  }

  async refresh(): Promise<void> {
    if (this._isRefreshing) {
      await firstValueFrom(this._refreshDone$.pipe());
      return;
    }
    this._isRefreshing = true;
    this._refreshDone$.next(false);
    try {
      const refreshToken = await this.getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token stored.');
      const auth = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${environment.apiUrl}/auth/refresh`,
          JSON.stringify(refreshToken),
          {
            headers: { 'Content-Type': 'application/json' },
          },
        ),
      );
      await this.processAuth(auth);
      this._refreshDone$.next(true);
    } finally {
      this._isRefreshing = false;
    }
  }

  async getPreviousSignIn(): Promise<string | null> {
    const response = await firstValueFrom(
      this.http.get<{ previousSignInAt: string | null }>(
        `${environment.apiUrl}/auth/previous-signin`,
      ),
    );
    return response?.previousSignInAt ?? null;
  }

  /**
   * Swap the current session for an impersonation session acting as the given
   * user. Stashes the helpdesk operator's token in sessionStorage so
   * {@link stopImpersonation} can restore it. No refresh token is issued —
   * the session ends when the access token expires.
   */
  async impersonate(userId: number): Promise<void> {
    const currentToken = this.getToken();
    if (!currentToken) throw new Error('Not signed in.');

    const auth = await firstValueFrom(
      this.http.post<AuthResponse>(`${environment.apiUrl}/auth/impersonate/${userId}`, {}),
    );

    // Save the helpdesk operator's token under a separate key so we can flip
    // back later without re-authenticating. We do NOT pass this through
    // StorageService (the typeahead banner needs it available synchronously
    // from sessionStorage, matching how the JWT interceptor reads it).
    sessionStorage.setItem('helpdesk_operator_token', currentToken);

    // Keep the operator's refresh token untouched: if the impersonation access
    // token expires, the interceptor's refresh naturally falls back to the
    // operator session (the helpdesk user simply re-emerges).
    sessionStorage.setItem('token', auth.accessToken);
  }

  /** True when a helpdesk operator token is stashed (i.e. we're impersonating). */
  isImpersonating(): boolean {
    return sessionStorage.getItem('helpdesk_operator_token') !== null;
  }

  /**
   * Restore the stashed helpdesk operator token and drop the impersonation
   * token. The caller is responsible for reloading user state (e.g. via
   * <code>CurrentUserStore</code>) and navigating.
   */
  stopImpersonation(): void {
    const stashed = sessionStorage.getItem('helpdesk_operator_token');
    if (!stashed) return;
    sessionStorage.setItem('token', stashed);
    sessionStorage.removeItem('helpdesk_operator_token');
  }

  async logout(): Promise<void> {
    const refreshToken = await this.getRefreshToken();
    if (refreshToken) {
      try {
        await firstValueFrom(
          this.http.post(`${environment.apiUrl}/auth/logout`, JSON.stringify(refreshToken), {
            headers: { 'Content-Type': 'application/json' },
          }),
        );
      } catch {
        // Best-effort: revoke server-side if possible, always clear client state
      }
    }
    this.clearAuth();
  }

  clearAuth(): void {
    sessionStorage.removeItem('token');
    this.storage.clear();
  }

  private async processAuth(auth: AuthResponse): Promise<UserInfo> {
    const payload = this.decodeJwtPayload(auth.accessToken);
    const user: UserInfo = {
      userId: payload['sub'] ?? '',
      username: payload['unique_name'] ?? '',
      name: payload['name'] ?? '',
      firstName: payload['given_name'] ?? '',
      lastName: payload['family_name'] ?? '',
      email: payload['email'] ?? '',
      role: payload['role'] ?? '',
    };
    sessionStorage.setItem('token', auth.accessToken);
    await this.storage.add('refreshToken', auth.refreshToken);
    await this.storage.add('authUser', user);
    return user;
  }

  private decodeJwtPayload(token: string): Record<string, string> {
    try {
      const payloadBase64 = token.split('.')[1];
      const padded = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(padded);
      return JSON.parse(decoded) as Record<string, string>;
    } catch {
      return {};
    }
  }
}

/** The 403 "2fa_required" login response, or null for any other error. */
function extractTwoFactorChallenge(err: unknown): TwoFactorRequired | null {
  if (!(err instanceof HttpErrorResponse) || err.status !== 403) return null;
  const body = err.error as Partial<TwoFactorRequired> | null;
  if (body?.code !== '2fa_required' || !body.challengeToken || !body.method) return null;
  return {
    code: body.code,
    method: body.method,
    challengeToken: body.challengeToken,
    maskedEmail: body.maskedEmail ?? null,
  };
}
